<template>
  <Card :class="$style.recommendedCard">
    <template v-slot:header>
      <div :class="$style.cardHeader">
        <div :class="$style.headerContent">
          <Icon
            :class="$style.infoIcon"
            icon="info"
            inline
            :scale="1"
          />
          <ParagraphBody>
            <strong>Recommended Next Step:</strong>
            {{ checklistHeader }}
          </ParagraphBody>
        </div>
        <div :class="$style.headerContent">
          <ButtonSecondary
            :class="$style.addButton"
            :data-test-id="$testId('addButton')"
            :text="showChecklistResource ? 'Add Feature' : 'Add'"
            @click="addFeature"
          />
          <IconButton
            aria-label="Expand recommended next step"
            :data-test-id="$testId('toggle')"
            hasNoBackground
            :icon="collapsed ? 'chevronDown' : 'chevronUp'"
            :scale="0.5"
            @click="toggleCollapsed"
          />
        </div>
      </div>
    </template>

    <template v-slot:body>
      <TransitionWrapper
        name="slideVertical"
      >
        <div
          v-if="!collapsed"
          :class="$style.cardBody"
        >
          <div>
            <ImplementationChecklistTask
              v-for="(task, index) in checklist.tasks"
              :key="task.headerText"
              :checklist="checklist"
              :task="task"
              :taskIndex="index"
              @event="triggerSegmentEvent"
            />
          </div>
          <ChecklistResource
            v-if="showChecklistResource"
            :data-test-id="$testId(createDataTestId(checklist.feature))"
            :feature="checklist.feature"
          />
        </div>
      </TransitionWrapper>
    </template>
  </Card>
</template>

<script>
import {
  ButtonSecondary,
  Card,
  Icon,
  IconButton,
  ParagraphBody,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { implementationChecklistRegion } from 'client/components/ImplementationChecklist/ImplementationChecklistConstants';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import {
  keepGoingNotificationId,
  onboardingFeatureDisplayNames,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import ChecklistResource from 'client/components/ConsolidatedChecklist/components/ChecklistResource.vue';
import Util from 'client/util/Util';

import ImplementationChecklistTask from './ImplementationChecklistTask.vue';

const onboardingModule = 'OnboardingModule';

export default {
  name: 'RecommendedChecklistItem',

  components: {
    ButtonSecondary,
    Card,
    ChecklistResource,
    Icon,
    IconButton,
    ImplementationChecklistTask,
    ParagraphBody,
    TransitionWrapper,
  },

  props: {
    checklist: {
      required: true,
      type: Object,
      validator: function validator(value) {
        if (!value) {
          return false;
        }

        if (!value.tasks || !value.feature) {
          return false;
        }

        return true;
      },
    },
    showChecklistResource: {
      required: true,
      type: Boolean,
    },
  },

  data() {
    return {
      collapsed: true,
    };
  },

  computed: {
    checklistHeader() {
      return implementationChecklists[this.checklist.feature]
        ? implementationChecklists[this.checklist.feature].headerText : this.checklist.headerText;
    },
  },

  methods: {
    ...mapActions(onboardingModule, [
      'addFeatureToImplementationChecklist',
    ]),

    ...mapActions(notificationModule, [
      'removeNotification',
    ]),

    addFeature() {
      this.addFeatureToImplementationChecklist(this.checklist);
      UiEvents.triggerButtonClicked({
        description: `Adds ${onboardingFeatureDisplayNames.deviceManagement} feature to checklist`,
        page: this.$page,
        region: implementationChecklistRegion,
        section: 'Consolidated Checklist',
        text: this.showChecklistResource ? 'Add Feature' : 'Add',
      });
      this.removeNotification({ id: keepGoingNotificationId });
    },

    createDataTestId(humanReadableString) {
      return Util.createDataTestId(humanReadableString);
    },

    toggleCollapsed(ev, collapsedEventValue) {
      this.collapsed = collapsedEventValue || !this.collapsed;
    },

    triggerSegmentEvent(event) {
      this.$emit('event', event);
    },
  },
};
</script>

<style scoped module>
.recommendedCard {
  padding: 0;
}

/** Override extra padding on card header. */
.recommendedCard /deep/ header {
  padding-bottom: 0;
}

.cardHeader {
  background-color: var(--jc-color-informational-surface);
  display: flex;
  justify-content: space-between;
  padding: var(--jc-spacer);
}

.cardBody {
  display: flex;
  padding: var(--jc-spacer-large);
}

.headerContent {
  align-items: center;
  display: flex;
}

.infoIcon {
  color: var(--jc-color-informational-base);
}

.addButton {
  --jc-button-min-width: auto;
}
</style>
